/**
 * LAE Productivity trend card: "Daily activity" / "Weekly activity", a metric picker and the column chart.
 *
 * Ports LAE `features/productivity/SchoolsView.jsx` (`renderTrend`, `chartSeries`, `Segmented`,
 * `sanitizeTrend`) and the trend block of `SchoolModal.jsx` (`TREND_METRICS_ADMIT`, `DIRECTION_SERIES`).
 * One component serves both: `memoryPrefix` keeps the page and the modal choices apart.
 *
 * Deliberate differences from LAE:
 * - The tab hover help is dropped (no helper text); the tabs keep LAE's labels.
 * - The picker is `SegmentedControl` from md and a native `Select` below it (six segments do not fit a
 *   phone header and the control does not wrap). At half width it is always the `Select`.
 * - The metric and the hidden legend keys are remembered per account with `useRememberedState`
 *   (`<prefix>-trend-metric`, `<prefix>-trend-hidden`) behind tolerant parses, instead of LAE's
 *   layout-document `view` field.
 * - The hover "Rate" row is shown for Total Calls Dialled only (`showRate`): LAE also printed it for
 *   Messages, where emails over whatsapps means nothing.
 * - Values are read by bucket rather than by array position, so a short or reordered series cannot
 *   shift bars. A missing point or null value draws as 0, as in LAE (the series is zero-filled).
 */
import { useId, useMemo } from 'react';
import { Panel, PanelBody, PanelHeader, SegmentedControl, Select } from '@/components/ui';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import type { LaeProdSeriesPoint } from '@/lib/types';
import { ColumnChart } from './ColumnChart';
import { fmtDay, fmtDayLong, fmtNum, talkRows } from './lae-format';
import { DIRECTION_FILL, SERIES_FILL, TREND_TALK_KEYS, metricLabel } from './lae-metrics';

/** The chart's metric tabs, in LAE order. `key` is stored on the account: never rename one. */
// oxlint-disable-next-line react/only-export-components
export const TREND_METRICS = [
  { key: 'calls', label: 'Total Calls Dialled' },
  { key: 'advanced', label: 'Positive Status Changes' },
  { key: 'regressed', label: 'Negative Status Changes' },
  { key: 'applications', label: 'Applications' },
  { key: 'worked', label: 'Leads Worked On' },
  { key: 'messages', label: 'Messages' },
] as const;

export type TrendMetric = (typeof TREND_METRICS)[number]['key'];

const DEFAULT_METRIC: TrendMetric = 'calls';

/** Series points carry no matriculation, so the admit funnel drops Applications (LAE `TREND_METRICS_ADMIT`). */
const TREND_METRICS_ADMIT = TREND_METRICS.filter((m) => m.key !== 'applications');

/** Status-change tabs: their fill and the subset named on their talk rows. */
const DIRECTION_SERIES: Partial<Record<TrendMetric, { fill: string; talk: string }>> = {
  advanced: { fill: DIRECTION_FILL.positive, talk: 'Positive Changes' },
  regressed: { fill: DIRECTION_FILL.negative, talk: 'Negative Changes' },
};

const NO_HIDDEN: string[] = [];

function isTrendMetric(value: unknown): value is TrendMetric {
  return typeof value === 'string' && TREND_METRICS.some((m) => m.key === value);
}

/** A remembered metric: one of the tabs, else rejected (the default applies). */
function parseTrendMetric(raw: unknown): TrendMetric | undefined {
  return isTrendMetric(raw) ? raw : undefined;
}

/** Remembered hidden legend keys: strings only, de-duplicated; anything else is rejected. */
function parseStringArray(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const keys = raw.filter((k): k is string => typeof k === 'string' && k !== '');
  return [...new Set(keys)].slice(0, 20);
}

type SeriesKey = keyof LaeProdSeriesPoint & string;

/** A count from a series point; null, missing or junk draws as 0 (the upstream zero-fills the series). */
function countOf(point: LaeProdSeriesPoint | undefined, key: SeriesKey): number {
  const raw: unknown = point ? point[key] : undefined;
  const n = typeof raw === 'number' ? raw : Number(raw ?? 0);
  return Number.isFinite(n) ? n : 0;
}

interface TrendSeries {
  key: string;
  label: string;
  values: number[];
  fill: string;
}

export interface TrendCardProps {
  /** `totals.series`: one point per bucket. */
  series: readonly LaeProdSeriesPoint[] | null | undefined;
  /** `window.buckets`; falls back to the series' own buckets. */
  buckets: readonly string[] | null | undefined;
  granularity: 'day' | 'week';
  /** A bar was clicked: the bucket's start date (`YYYY-MM-DD`). */
  onBucketClick?: (bucket: string) => void;
  /** Chart height in px: 240 on the page, 200 in the school modal. */
  height: number;
  /** The admit-to-enrolment funnel: Applications is not offered. */
  admit?: boolean;
  /** Keeps the page and the school modal choices apart on the account. */
  memoryPrefix: 'lae' | 'lae-modal';
  /** Rendered in a half-width layout block: the picker is always the compact select. */
  half?: boolean;
}

export function TrendCard({
  series,
  buckets,
  granularity,
  onBucketClick,
  height,
  admit = false,
  memoryPrefix,
  half = false,
}: TrendCardProps) {
  const titleId = useId();
  const [storedMetric, setMetric] = useRememberedState<TrendMetric>(
    `${memoryPrefix}-trend-metric`,
    DEFAULT_METRIC,
    parseTrendMetric,
  );
  const [storedHidden, setHidden] = useRememberedState<string[]>(
    `${memoryPrefix}-trend-hidden`,
    NO_HIDDEN,
    parseStringArray,
  );

  const metrics = admit ? TREND_METRICS_ADMIT : TREND_METRICS;
  // An unavailable remembered tab shows as the default without overwriting what was remembered.
  const metric: TrendMetric = metrics.some((m) => m.key === storedMetric) ? storedMetric : DEFAULT_METRIC;

  const bucketList = useMemo(
    () => (buckets && buckets.length ? [...buckets] : (series ?? []).map((p) => p.bucket)),
    [buckets, series],
  );
  const pointOf = useMemo(() => {
    const byBucket = new Map<string, LaeProdSeriesPoint>();
    (series ?? []).forEach((p) => byBucket.set(p.bucket, p));
    return byBucket;
  }, [series]);

  const chartSeries = useMemo<TrendSeries[]>(() => {
    const pick = (key: SeriesKey) => bucketList.map((b) => countOf(pointOf.get(b), key));
    if (metric === 'messages') {
      return [
        { key: 'whatsapp_prepared', label: metricLabel('whatsapp_prepared'), values: pick('whatsapp_prepared'), fill: SERIES_FILL[0] },
        { key: 'emails_sent', label: metricLabel('emails_sent'), values: pick('emails_sent'), fill: SERIES_FILL[1] },
      ];
    }
    const label = TREND_METRICS.find((m) => m.key === metric)?.label ?? metric;
    const out: TrendSeries[] = [
      { key: metric, label, values: pick(metric), fill: DIRECTION_SERIES[metric]?.fill ?? SERIES_FILL[0] },
    ];
    if (metric === 'calls') {
      out.push({ key: 'connected', label: metricLabel('connected'), values: pick('connected'), fill: SERIES_FILL[1] });
    }
    return out;
  }, [bucketList, pointOf, metric]);

  // Only keys of the shown series count, and never all of them: one series always stays visible.
  const seriesKeys = chartSeries.map((s) => s.key);
  const hiddenHere = storedHidden.filter((k) => seriesKeys.includes(k));
  const hidden = chartSeries.length > 1 && hiddenHere.length < chartSeries.length ? hiddenHere : NO_HIDDEN;
  const onHiddenChange = (next: string[]) => {
    // Keys of the other tabs' series stay remembered while this tab's legend changes.
    const others = storedHidden.filter((k) => !seriesKeys.includes(k));
    setHidden([...others, ...next.filter((k) => seriesKeys.includes(k))]);
  };

  const talkKeys = metric in TREND_TALK_KEYS ? TREND_TALK_KEYS[metric as keyof typeof TREND_TALK_KEYS] : null;
  const talkSuffix = DIRECTION_SERIES[metric]?.talk;
  const tooltipRows = talkKeys
    ? (i: number) => {
        const bucket = bucketList[i];
        return talkRows(bucket === undefined ? undefined : pointOf.get(bucket), talkKeys[0], talkKeys[1], talkSuffix);
      }
    : undefined;

  const period = granularity === 'week' ? 'Weekly' : 'Daily';
  const ariaLabel = `${period} ${metric === 'messages' ? 'Whatsapps Sent and Emails Sent' : (chartSeries[0]?.label ?? '')}`;
  const formatTooltipX = granularity === 'week' ? (b: string) => `Week of ${fmtDay(b)}` : (b: string) => fmtDayLong(b);

  const options = metrics.map((m) => ({ value: m.key, label: m.label }));
  const select = (
    <Select
      aria-label="Chart metric"
      value={metric}
      onChange={(e) => {
        const next = e.target.value;
        if (isTrendMetric(next)) setMetric(next);
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );

  const picker = half ? (
    select
  ) : (
    <>
      <div className="md:hidden">{select}</div>
      <div className="hidden md:block">
        <SegmentedControl<TrendMetric>
          label="Chart metric"
          value={metric}
          options={options}
          onChange={setMetric}
          className="w-auto"
        />
      </div>
    </>
  );

  return (
    <Panel aria-labelledby={titleId} className="h-full min-w-0">
      <PanelHeader
        id={titleId}
        title={granularity === 'week' ? 'Weekly activity' : 'Daily activity'}
        actions={picker}
      />
      <PanelBody>
        <ColumnChart
          buckets={bucketList}
          series={chartSeries}
          height={height}
          formatX={(b: string) => fmtDay(b)}
          formatTooltipX={formatTooltipX}
          formatValue={(v: number) => fmtNum(v)}
          {...(onBucketClick ? { onBucketClick } : {})}
          {...(tooltipRows ? { tooltipRows } : {})}
          hidden={hidden}
          onHiddenChange={onHiddenChange}
          showRate={metric === 'calls'}
          ariaLabel={ariaLabel}
        />
      </PanelBody>
    </Panel>
  );
}
